import { ref, computed, onUnmounted } from 'vue';

/**
 * useCountdown — Countdown timer reutilizable basado en Composition API.
 *
 * Acepta dos modos de inicialización:
 *   - `durationMs`: duración en milisegundos desde que se llama a `start()`.
 *   - `endsAt`: timestamp (ms) o Date que indica cuándo expira.
 *
 * Ambas opciones son mutuamente excluyentes; si se pasan las dos, `endsAt` tiene prioridad.
 *
 * API pública:
 *   // Estado reactivo
 *   minutesLeft   — ref<number>  — minutos restantes (entero, ≥ 0)
 *   secondsLeft   — ref<number>  — segundos restantes dentro del minuto (0–59)
 *   totalMsLeft   — ref<number>  — milisegundos totales restantes (≥ 0)
 *   isExpired     — ref<boolean> — true cuando el timer llega a 0
 *   isRunning     — ref<boolean> — true mientras el intervalo está activo
 *   percentLeft   — computed<number> — porcentaje restante (0–100) respecto a la duración original
 *   formatted     — computed<string> — "MM:SS" listo para renderizar
 *
 *   // Acciones
 *   start()       — arranca el timer (o lo reinicia si ya expiró)
 *   stop()        — pausa el timer sin resetear el estado
 *   reset()       — para el timer y vuelve al estado inicial (no arranca automáticamente)
 *   onExpired(cb) — registra un callback que se llama UNA VEZ cuando expira
 *
 * Notas de diseño:
 *   - Usa `Date.now()` (nativo) para calcular diferencias — sin moment.js ni dayjs.
 *   - El intervalo es de 500ms para reducir drift visual (actualiza cada 500ms).
 *   - `percentLeft` solo es significativo cuando se pasa `durationMs`; con `endsAt`
 *     sin `durationMs` devuelve 0 porque no se conoce la duración original.
 *   - El intervalo se limpia en `onUnmounted` automáticamente.
 *   - `onExpired` callbacks se limpian internamente al expirar para evitar referencias colgadas.
 *
 * @param {Object} options
 * @param {number} [options.durationMs]  — duración total en ms (ej. 15 * 60 * 1000 para 15 min)
 * @param {number|Date} [options.endsAt] — timestamp ms o Date de expiración
 * @param {boolean} [options.autoStart=false] — si true, arranca el timer al crear el composable
 *
 * @returns {{ minutesLeft, secondsLeft, totalMsLeft, isExpired, isRunning, percentLeft, formatted, start, stop, reset, onExpired }}
 */
export function useCountdown({ durationMs, endsAt, autoStart = false } = {}) {
    // ─── Duración original (para percentLeft) ─────────────────────────────────
    const _originalDurationMs = ref(
        endsAt
            ? Math.max(0, _resolveEndsAt(endsAt) - Date.now())
            : (durationMs ?? 0)
    );

    // ─── Estado reactivo ──────────────────────────────────────────────────────
    const totalMsLeft = ref(_originalDurationMs.value);
    const isExpired   = ref(false);
    const isRunning   = ref(false);

    // ─── Derived ──────────────────────────────────────────────────────────────
    const minutesLeft = computed(() => Math.floor(totalMsLeft.value / 60_000));
    const secondsLeft = computed(() => Math.floor((totalMsLeft.value % 60_000) / 1_000));
    const percentLeft = computed(() => {
        if (_originalDurationMs.value <= 0) return 0;
        return Math.round((totalMsLeft.value / _originalDurationMs.value) * 100);
    });
    const formatted = computed(() => {
        const m = minutesLeft.value;
        const s = secondsLeft.value;
        return `${m}:${String(s).padStart(2, '0')}`;
    });

    // ─── Internals ────────────────────────────────────────────────────────────
    let _intervalId   = null;
    let _targetMs     = null; // timestamp absoluto en ms cuando expira
    const _expiredCbs = [];

    function _resolveTarget() {
        if (endsAt) {
            return _resolveEndsAt(endsAt);
        }
        return Date.now() + totalMsLeft.value;
    }

    function _tick() {
        const remaining = Math.max(0, _targetMs - Date.now());
        totalMsLeft.value = remaining;

        if (remaining <= 0) {
            _expire();
        }
    }

    function _expire() {
        _clearInterval();
        isRunning.value  = false;
        isExpired.value  = true;
        totalMsLeft.value = 0;

        // Notificar callbacks registrados
        _expiredCbs.forEach(cb => cb());
        _expiredCbs.length = 0;
    }

    function _clearInterval() {
        if (_intervalId !== null) {
            clearInterval(_intervalId);
            _intervalId = null;
        }
    }

    // ─── API pública ──────────────────────────────────────────────────────────

    /**
     * Arranca el timer. Si ya estaba corriendo, no hace nada.
     * Si estaba pausado, reanuda desde el tiempo restante actual.
     */
    function start() {
        if (isRunning.value) return;

        // Si ya expiró y se llama start() de nuevo, reset implícito
        if (isExpired.value) {
            reset();
        }

        isExpired.value = false;
        isRunning.value = true;
        _targetMs = _resolveTarget();
        _tick(); // tick inmediato para evitar el primer segundo de lag
        _intervalId = setInterval(_tick, 500);
    }

    /**
     * Pausa el timer sin resetear el tiempo restante.
     */
    function stop() {
        if (!isRunning.value) return;
        _clearInterval();
        isRunning.value = false;
    }

    /**
     * Para el timer y resetea el estado al inicial (no arranca automáticamente).
     * Útil para reutilizar el composable en el mismo ciclo de vida.
     */
    function reset() {
        _clearInterval();
        isRunning.value   = false;
        isExpired.value   = false;
        _originalDurationMs.value = durationMs ?? 0;
        totalMsLeft.value = _originalDurationMs.value;
        _targetMs = null;
    }

    /**
     * Registra un callback que se invocará UNA VEZ cuando el timer expire.
     * @param {Function} cb
     */
    function onExpired(cb) {
        if (typeof cb === 'function') {
            _expiredCbs.push(cb);
        }
    }

    // Limpieza automática al desmontar el componente que usa el composable
    onUnmounted(() => {
        _clearInterval();
        _expiredCbs.length = 0;
    });

    // Arranque automático opcional
    if (autoStart) {
        start();
    }

    return {
        // Estado
        minutesLeft,
        secondsLeft,
        totalMsLeft,
        isExpired,
        isRunning,
        // Computed
        percentLeft,
        formatted,
        // Acciones
        start,
        stop,
        reset,
        onExpired,
    };
}

// ─── Helper interno ───────────────────────────────────────────────────────────

/**
 * Normaliza `endsAt` (Date | number) a timestamp en ms.
 * @param {Date|number} endsAt
 * @returns {number}
 */
function _resolveEndsAt(endsAt) {
    if (endsAt instanceof Date) return endsAt.getTime();
    return Number(endsAt);
}
